import { Box, Stack, Typography } from '@mui/material';
import {
  getScannerBeamLineSx,
  getScannerCodeLineSx,
  getScannerScrollingCodeContainerSx,
  scannerAddressBarSx,
  scannerBeamPositionerSx,
  scannerBrowserChromeSx,
  scannerColumnOuterSx,
  scannerDotSx,
  scannerLineNumberSx,
  scannerSectionLabelDotSx,
  scannerSectionLabelRowSx,
  scannerSectionLabelTypographySx,
  scannerViewportSx,
} from './ScannerAnimation.styles';
import type { LineStyle, ScannerColumnProps } from './types';

function getLineColor(lineStyle: LineStyle): string {
  switch (lineStyle) {
    case 'normal':
      return 'rgba(0,0,0,0.30)';
    case 'bright':
      return 'rgba(0,0,0,0.50)';
    case 'keyword':
      return '#2563eb';
    case 'string':
      return '#16a34a';
    case 'comment':
      return 'rgba(0,0,0,0.18)';
    case 'trigger':
      return 'rgba(0,0,0,0.55)';
    case 'dim':
    default:
      return 'rgba(0,0,0,0.12)';
  }
}

export function ScannerColumn({
  currentHost,
  codeLines,
  scanOffset,
  flashingLines,
  hitLines,
  beamFlash,
  viewportRef,
}: ScannerColumnProps) {
  return (
    <Box sx={scannerColumnOuterSx}>
      {/* Browser chrome */}
      <Box sx={scannerBrowserChromeSx}>
        <Stack direction="row" spacing={0.5}>
          {['#ff5f57', '#febc2e', '#28c840'].map((dotColor) => {
            return (
              <Box key={dotColor} sx={{ ...scannerDotSx, bgcolor: dotColor }} />
            );
          })}
        </Stack>
        <Box sx={scannerAddressBarSx}>view-source:{currentHost}</Box>
      </Box>

      {/* Section label */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={scannerSectionLabelRowSx}>
        <Box sx={scannerSectionLabelDotSx} />
        <Typography sx={scannerSectionLabelTypographySx}>
          Source Inspector
        </Typography>
      </Stack>

      {/* Code viewport */}
      <Box ref={viewportRef} sx={scannerViewportSx}>
        {/* Beam */}
        <Box sx={scannerBeamPositionerSx}>
          <Box sx={getScannerBeamLineSx(beamFlash)} />
        </Box>

        {/* Scrolling code */}
        <Box sx={getScannerScrollingCodeContainerSx(scanOffset)}>
          {codeLines.map((codeLine, lineIndex) => {
            const isFlashing = flashingLines.has(lineIndex);
            const isHit = hitLines.has(lineIndex);
            let rowBackgroundColor = 'transparent';
            if (isFlashing) {
              rowBackgroundColor = 'rgba(26,86,255,0.16)';
            } else if (isHit) {
              rowBackgroundColor = 'rgba(26,86,255,0.07)';
            }
            return (
              <Box
                key={lineIndex}
                sx={getScannerCodeLineSx(
                  rowBackgroundColor,
                  getLineColor(codeLine[1]),
                  codeLine[1] === 'comment'
                )}>
                <Typography component="span" sx={scannerLineNumberSx}>
                  {lineIndex + 1}
                </Typography>
                {codeLine[0]}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
